import { MouseEvent, ReactNode, Ref, forwardRef, useContext } from "react";
import { Link, Navigate } from "react-router-dom";

import { UserCompanyConfigurationContext } from "@/app/providers";

import { UserCompanyLink } from "@/model/user-company-configuration/UserCompanyConfiguration";

export interface ISATOSLinkProps {
	className?: string;
	params?: Array<string | number | undefined>;
	redirect?: boolean;
	search?: string;
	serverSideRendering?: boolean;
	stopPropagation?: boolean;
	target?: "_blank" | "";
	to: string;
	onClick?: () => void;
	onClickAsync?: () => Promise<unknown>;
	children?: ReactNode;
}

export const SATOSLink = forwardRef<HTMLAnchorElement, ISATOSLinkProps>(
	(props, ref) => {
		const userCompanyConfiguration = useContext(
			UserCompanyConfigurationContext,
		);

		const linkRules = userCompanyConfiguration?.Links;

		const handleOnClick = async (event: MouseEvent): Promise<void> => {
			if (props.stopPropagation) {
				event.stopPropagation();
			}

			if (props.onClick) {
				props.onClick();
			}

			if (props.onClickAsync) {
				await props.onClickAsync();
			}
		};

		// TODO: Abstract to helper file.
		const formatUrl = (
			linkRule: UserCompanyLink,
			params: Array<string | number | undefined> | undefined,
		) => {
			if (!params || params.length === 0) {
				return linkRule.Url;
			}

			return linkRule?.Url
				? linkRule.Url.replace(/{(\d+)}/g, (_, index: string) => {
						const paramIndex = Number.parseInt(index, 10);
						if (Number.isNaN(paramIndex)) return "";
						return params[paramIndex]?.toString() ?? "";
					})
				: "";
		};

		const getLinkElement = (options: {
			to: string;
			search?: string;
			children?: ReactNode;
			serverSideRendering?: boolean;
			target?: "_blank" | "";
		}) => {
			const currentOrigin =
				typeof window !== "undefined"
					? `${window.location.protocol}//${window.location.host}`
					: "";
			const isSameOriginAbsolute =
				currentOrigin.length > 0 && options.to.startsWith(currentOrigin);
			const isInternalRelative = options.to.startsWith("/");
			const rel =
				options.target === "_blank" ? "noopener noreferrer" : undefined;
			if (isSameOriginAbsolute || isInternalRelative) {
				// Split pathname and search (e.g. "/shift?tab=dashboard" -> pathname="/shift", search="?tab=dashboard")
				let pathname = options.to;
				let search = options.search;
				const qIndex = pathname.indexOf("?");
				if (qIndex !== -1) {
					search = search ?? pathname.slice(qIndex); // "?tab=dashboard"
					pathname = pathname.slice(0, qIndex);
				} else {
					// React Router disallows '?' in pathname; encode for filenames with literal '?'
					pathname = pathname.replace(/\?/g, "%3F");
				}
				const linkTo = search != null ? { pathname, search } : pathname;
				return (
					<Link
						ref={ref as Ref<HTMLAnchorElement>}
						className={props.className}
						onClick={(event: MouseEvent) => {
							void handleOnClick(event);
						}}
						to={linkTo}
						target={options.target}
						replace={isSameOriginAbsolute}
						rel={rel}
					>
						{options?.children}
					</Link>
				);
			}

			return (
				<a
					ref={ref}
					className={props.className}
					onClick={(event: MouseEvent) => {
						void handleOnClick(event);
					}}
					href={options.to}
					target={options.target}
					rel={rel}
				>
					{options?.children}
				</a>
			);
		};

		if (!linkRules) {
			return getLinkElement(props);
		}

		const linkRule = linkRules[props.to];

		if (linkRule) {
			const url = linkRule ? formatUrl(linkRule, props.params) : props.to;

			if (props.redirect !== undefined) {
				if (props.redirect && linkRule.Local) {
					// nosemgrep
					return (
						<Navigate to={{ pathname: `/${url}`, search: props.search }} />
					);
				} else if (props.redirect) {
					window.location.href = `${linkRule.BaseUrl}${encodeURI(url)}`; // nosemgrep
				} else {
					return null;
				}
			}

			if (linkRule.Local && props.serverSideRendering) {
				return (
					<a
						ref={ref}
						className={props.className}
						onClick={(event: MouseEvent) => {
							void handleOnClick(event);
						}}
						href={`${window.location.protocol}//${
							window.location.host
						}/${encodeURI(url)}`}
						target={props.target}
						rel={props.target === "_blank" ? "noopener noreferrer" : undefined}
					>
						{props.children}
					</a>
				);
			}

			if (linkRule.Local) {
				return (
					<Link
						ref={ref as Ref<HTMLAnchorElement>}
						className={props.className}
						onClick={(event: MouseEvent) => {
							void handleOnClick(event);
						}}
						to={{ pathname: `/${url}`, search: props.search }}
						target={props.target}
						rel={props.target === "_blank" ? "noopener noreferrer" : undefined}
					>
						{props.children}
					</Link>
				);
			}

			if (window && window.location.protocol.startsWith("http")) {
				return (
					<a
						ref={ref}
						className={props.className}
						onClick={(event: MouseEvent) => {
							void handleOnClick(event);
						}}
						href={`${linkRule.BaseUrl}${url}`}
						target={props.target}
						rel={props.target === "_blank" ? "noopener noreferrer" : undefined}
					>
						{props.children}
					</a>
				);
			}
			return (
				<a
					ref={ref}
					className={props.className}
					onClick={(event: MouseEvent) => {
						void handleOnClick(event);
					}}
					href={url}
					target={props.target}
					rel={props.target === "_blank" ? "noopener noreferrer" : undefined}
				>
					{props.children}
				</a>
			);
		}

		return getLinkElement(props);
	},
);

SATOSLink.displayName = "SATOSLink";
